// components/Profile/ProtectedRoute.tsx
import React, { useState, useRef, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Alert, Button, CircularProgress, Box } from '@mui/material';
import { config } from 'shared/config'; // Adjust path as needed
import { readSharedResource } from 'shared/api/readResource';
import { ensureSession, SESSION_CHANGED, sessionExpired, type Session } from 'shared/api/session';
import { useEventListener, useLoad } from 'shared/hooks';
import { rememberAuthenticationReturn, rememberOrganizationReturn } from './collaborationReturn';

export type RoutePrefetch = (context: { userId: string; pathname: string; search: string }) => void;

interface ProtectedRouteProps {
    children: ReactNode;
    requireSubscription?: boolean;
    /** Reads the page will need, started as soon as the user is known (in parallel with the access checks). */
    prefetch?: RoutePrefetch;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireSubscription = true, prefetch }) => {
    const [status, setStatus] = useState<'loading' | 'signed_out' | 'active' | 'inactive' | 'error'>('loading');
    const [attempt, setAttempt] = useState(0);
    const location = useLocation();
    const wasActive = useRef(false);
    const prefetchScope = useRef({ prefetch, pathname: location.pathname, search: location.search });
    prefetchScope.current = { prefetch, pathname: location.pathname, search: location.search };

    useEventListener(SESSION_CHANGED, () => setAttempt(value => value + 1));

    useLoad((signal) => {

        const checkAuth = async () => {
            setStatus('loading');
            try {
                const { user } = await ensureSession();
                const scope = prefetchScope.current;
                if (scope.prefetch && !signal.aborted && attempt === 0) {
                    try {
                        scope.prefetch({ userId: user.userId, pathname: scope.pathname, search: scope.search });
                    } catch {
                        // A prefetch never blocks the route; the page reads for itself.
                    }
                }
                if (!requireSubscription) {
                    if (!signal.aborted) { wasActive.current = true; setStatus('active'); }
                    return;
                }
                // The server's decision (demo accounts count as subscribed), read fresh from the session; null: not checked now.
                const response = await readSharedResource(
                    `identity:subscription:${user.userId}`,
                    () => axios.get<Session>(`${config.API_URL}/api/auth/session`, { withCredentials: true }),
                    { cacheMs: 5_000, force: attempt > 0 },
                );
                const subscribed = response.data.subscribed;
                if (!signal.aborted) {
                    if (subscribed === true) wasActive.current = true;
                    setStatus(subscribed === true ? 'active' : subscribed === false ? 'inactive' : 'error');
                }
            } catch (error: any) {
                if (!signal.aborted) setStatus(error.response?.status === 401 ? 'signed_out' : 'error');
            }
        };

        void checkAuth();

    }, [attempt, requireSubscription]);

    if (status === 'loading') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (status === 'error') {
        return (
            <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8, px: 2 }}>
                <Alert severity="error" action={<Button color="inherit" onClick={() => setAttempt(value => value + 1)}>Retry</Button>}>
                    We couldn’t check your account access. Please try again.
                </Alert>
            </Box>
        );
    }

    if (status === 'signed_out') {
        if (location.pathname === '/organization') rememberOrganizationReturn();
        // Sign-in returns to this exact place (path and query) when the session expired, or when the page was opened signed out
        // (a shared link, an app's connection request, a connection's credential page). Signing out does not.
        const expired = sessionExpired();
        if (expired || !wasActive.current) rememberAuthenticationReturn(location.pathname + location.search);
        return <Navigate to="/login" state={{ from: location, sessionExpired: expired }} replace />;
    }

    if (status === 'inactive') {
        return <Navigate to="/subscribe" state={{ from: location }} replace />;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
